import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore
} from "react";
import { Capacitor } from "@capacitor/core";

export const MIN_REFRESH_MS = 600;
export const MAX_REFRESH_MS = 15000;

interface PullToRefreshEntry {
  refresh: () => unknown;
  enabled: boolean;
}

interface PullToRefreshState {
  isEnabled: boolean;
  isRefreshing: boolean;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function createPullToRefreshStore(isNative: boolean) {
  const entries = new Set<PullToRefreshEntry>();
  const listeners = new Set<() => void>();
  let state: PullToRefreshState = { isEnabled: false, isRefreshing: false };
  let generation = 0;

  const setState = (next: PullToRefreshState) => {
    if (next.isEnabled === state.isEnabled && next.isRefreshing === state.isRefreshing) return;
    state = next;
    listeners.forEach((listener) => listener());
  };

  const sync = () => {
    const active = Array.from(entries);
    if (!active.length && state.isRefreshing) generation += 1;
    setState({
      isEnabled: isNative && active.length > 0 && active.every((entry) => entry.enabled),
      isRefreshing: active.length > 0 && state.isRefreshing
    });
  };

  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => state,
    sync,
    register(entry: PullToRefreshEntry) {
      entries.add(entry);
      sync();
      return () => {
        entries.delete(entry);
        sync();
      };
    },
    async refresh() {
      if (state.isRefreshing || !state.isEnabled) return;
      generation += 1;
      const current = generation;
      const startedAt = Date.now();
      setState({ ...state, isRefreshing: true });
      const work = Promise.allSettled(
        Array.from(entries)
          .filter((entry) => entry.enabled)
          .map((entry) => Promise.resolve().then(() => entry.refresh()))
      );
      await Promise.race([work, wait(MAX_REFRESH_MS)]);
      await wait(Math.max(0, MIN_REFRESH_MS - (Date.now() - startedAt)));
      if (generation === current) setState({ ...state, isRefreshing: false });
    }
  };
}

type PullToRefreshStore = ReturnType<typeof createPullToRefreshStore>;

const PullToRefreshContext = createContext<PullToRefreshStore>(createPullToRefreshStore(false));

export function PullToRefreshProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [store] = useState(() => createPullToRefreshStore(Capacitor.isNativePlatform()));
  return <PullToRefreshContext.Provider value={store}>{children}</PullToRefreshContext.Provider>;
}

export function usePullToRefresh(
  onRefresh: () => unknown,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const store = useContext(PullToRefreshContext);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const [entry] = useState<PullToRefreshEntry>(() => ({
    refresh: () => onRefreshRef.current(),
    enabled
  }));

  useEffect(() => store.register(entry), [store, entry]);

  useEffect(() => {
    entry.enabled = enabled;
    store.sync();
  }, [store, entry, enabled]);
}

export function usePullToRefreshHost() {
  const store = useContext(PullToRefreshContext);
  const { isEnabled, isRefreshing } = useSyncExternalStore(store.subscribe, store.getSnapshot);
  return { isEnabled, isRefreshing, refresh: store.refresh, getSnapshot: store.getSnapshot };
}
